import BotaoSimular from "@/components/BotaoSimular";
import { registerMetadata } from "@/services/metadataEvent";
import { Container, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useEffect } from "react";
import { isMobile } from "react-device-detect";
import { useSearchParams } from "react-router-dom";
import "./como-organizar-o-orcamento-mesmo-com-dividas.scss";

export default function ComoOrganizarOrcamentoMesmoComDividas() {
  const [searchParams] = useSearchParams();
  searchParams.get("__firebase_request_key");

  useEffect(() => {
    const metaData = {};
    metaData.utmSource = searchParams.get("utm_source");
    metaData.utmMedium = searchParams.get("utm_medium");
    metaData.utmCampaign = searchParams.get("utm_campaign");
    metaData.utmId = searchParams.get("utm_id");
    metaData.utmTerm = searchParams.get("utm_term");
    const metadataRegister = {
      ...metaData,
      isMobile: isMobile,
      userAction:
        "VIEW_BLOG_COMO_ORGANIZAR_ORCAMENTO_MESMO_COM_DIVIDAS_ACUMULADAS",
      url: window.location.href,
    };

    registerMetadata(metadataRegister, "METADATA_VIEW_SITE_QUITABOLETOS");
  }, [searchParams]);

  return (
    <Container className="sessao-post">
      <Grid
        size={12}
        sx={{
          display: "flex",
          justifyContent: "center",
        }}>
        <Grid container spacing={3} maxWidth="sm" sx={{ py: 3 }}>
          <Grid
            className="logo-blog"
            size={12}
            sx={{
              display: { xs: "none", sm: "flex" },
              justifyContent: "center",
            }}>
            <img
              className="logo-blog-img"
              src="/site/blog/blog_como_organizar_o_orcamento_do_mes_mesmo_com_dividas.png"
              alt="logo-blog"
            />
          </Grid>

          <Grid size={12}>
            <Typography variant="h1" sx={{ mt: 2, p: 1 }}>
              Como organizar o orçamento do mês mesmo com dívidas acumuladas
            </Typography>
          </Grid>

          <Grid size={12}>
            <Typography sx={{ fontSize: 22, fontStyle: "italic" }}>
              Organizar o orçamento do mês pode parecer uma missão impossível quando as dívidas se acumulam e o dinheiro mal dá conta do básico. Mas a verdade é que, com um <strong>planejamento financeiro simples</strong>, é possível retomar o controle — mesmo em meio ao caos. Não se trata de fórmulas milagrosas, e sim de escolhas estratégicas que cabem na sua realidade.
            </Typography>
            <Typography sx={{ fontSize: 22, mt: 2 }}>
              Neste conteúdo, você vai aprender como organizar o orçamento mesmo com dívidas, quais passos priorizar e por que soluções como o parcelamento podem oferecer um alívio necessário para recomeçar.
            </Typography>
          </Grid>

          <Grid container spacing={2} sx={{ mx: 1, mt: 2 }}>
            <Grid size={12}>
              <Typography sx={{ fontSize: 24, fontWeight: "bold", mb: 1 }}>
                1. Entenda sua situação financeira com clareza (sem medo)
              </Typography>
            </Grid>
            <Grid size={12}>
              <Typography sx={{ fontSize: 20, mb: 2 }}>
                Antes de qualquer passo, é essencial saber exatamente <strong>quanto você deve e para quem.</strong>
                {" "}Liste todas as suas dívidas, seus valores, juros e prazos. Em paralelo, anote também todos os seus ganhos fixos e variáveis.
                O objetivo aqui não é se culpar, mas sim <strong>encarar a realidade de frente</strong> para poder planejar com inteligência.
              </Typography>
              <Typography sx={{ fontSize: 20, mb: 2 }}>
                <strong>Dica prática:</strong> use uma planilha simples ou aplicativos gratuitos para visualizar suas entradas e saídas. O importante é ter tudo no mesmo lugar.
              </Typography>
            </Grid>

            <Grid size={12}>
              <Typography sx={{ fontSize: 24, fontWeight: "bold", mb: 1 }}>
                2. Organize seus gastos por prioridade
              </Typography>
            </Grid>
            <Grid size={12}>
              <Typography sx={{ fontSize: 20, mb: 2 }}>
                Com o levantamento completo em mãos, classifique suas despesas em três grupos:
              </Typography>

              <ul className="lista-blog">
                <li><strong>Essenciais:</strong> moradia, alimentação, transporte e saúde.
                </li>
                <li><strong>Importantes, mas adiáveis:</strong> cursos, assinatura de serviços, lazer.
                </li>
                <li><strong>Supérfluos:</strong> compras por impulso, delivery frequente, entre outros.
                </li>
              </ul>

              <Typography sx={{ fontSize: 20, mb: 2 }}>
                A ideia não é eliminar tudo de vez, mas <strong>reavaliar o que faz sentido manter agora</strong>
                {" "}e o que pode esperar até sua situação financeira estar mais estável.
              </Typography>
            </Grid>
            <Grid size={12}>
              <Typography sx={{ fontSize: 24, fontWeight: "bold", mb: 1 }}>
                3. Negocie suas dívidas
              </Typography>
            </Grid>
            <Grid size={12}>
              <Typography sx={{ fontSize: 20, mb: 2 }}>
                Muitos brasileiros mantêm dívidas que só crescem com o tempo. Se esse é o seu caso, vale buscar alternativas para <strong>negociar os débitos em condições melhores</strong>. Algumas empresas oferecem descontos à vista, outras aceitam parcelar com condições facilitadas.
              </Typography>
              <Typography sx={{ fontSize: 20, mb: 2 }}>
                Mas atenção: <strong>nem todo parcelamento é vantajoso.</strong>{" "}
                Avalie bem as taxas, o valor final e se a parcela realmente cabe no seu bolso.
              </Typography>
            </Grid>

            <Grid size={12}>
              <Typography sx={{ fontSize: 24, fontWeight: "bold", mb: 1 }}>
                4. Considere o parcelamento como estratégia de respiro
              </Typography>
            </Grid>
            <Grid size={12}>
              <Typography sx={{ fontSize: 20, mb: 2 }}>
                Quando bem usado, <strong>parcelar contas no cartão de crédito</strong> pode ser uma ferramenta útil para organizar seu fluxo de caixa.
                Imagine quitar um boleto à vista (como aluguel, faculdade ou IPVA) e,
                ao mesmo tempo, conseguir pagar em até 12x no cartão — sem comprometer todo o seu salário do mês.
              </Typography>
              <Typography sx={{ fontSize: 20, mb: 2 }}>
                Essa prática permite mais <strong>flexibilidade no orçamento</strong>,
                evita atrasos e pode ser o empurrão que faltava para colocar as contas em dia.
              </Typography>
              <Typography sx={{ fontSize: 20, mb: 2 }}>
                <strong>Exemplo real:</strong> com plataformas como a <span className="quita">Quita</span>,
                você pode parcelar boletos e Pix em até 12x no cartão de crédito, com transparência e segurança.
                É um alívio no presente e um planejamento melhor para o futuro.
              </Typography>
            </Grid>

            <Grid size={12}>
              <Typography sx={{ fontSize: 24, fontWeight: "bold", mb: 1 }}>
                5. Crie um orçamento realista (e flexível)
              </Typography>
            </Grid>
            <Grid size={12}>
              <Typography sx={{ fontSize: 20, mb: 2 }}>
                Agora que você entende sua situação, priorizou seus gastos e, se necessário,
                usou o parcelamento para reorganizar as contas,
                é hora de montar seu novo orçamento mensal.
              </Typography>

              <Typography sx={{ fontSize: 20, mb: 2 }}>
                Reserve valores fixos para:
              </Typography>

              <ul className="lista-blog">
                <li>Despesas essenciais</li>
                <li>Pagamento de dívidas/parcelamentos</li>
                <li>Fundo de emergência (mesmo que seja um valor simbólico)</li>
                <li>Pequenas alegrias do mês (você também merece!)</li>
              </ul>

              <Typography sx={{ fontSize: 20, mt: 2 }}>
                Lembre-se: <strong>um bom planejamento financeiro não precisa ser rígido</strong>,
                mas sim funcional e adaptável à sua vida.
              </Typography>
            </Grid>

            <Grid size={12}>
              <Typography sx={{ fontSize: 24, fontWeight: "bold", mb: 1 }}>
                6. Mantenha o controle e celebre pequenas conquistas
              </Typography>
            </Grid>
            <Grid size={12}>
              <Typography sx={{ fontSize: 20, mb: 2 }}>
                A organização do orçamento é um processo contínuo.
                Por isso, reserve um momento toda semana para revisar seus gastos,
                conferir o que pode ser ajustado e celebrar os avanços — mesmo os pequenos.
              </Typography>

              <Typography sx={{ fontSize: 20, mb: 2 }}>
                Aos poucos, você vai perceber que <strong>organizar o orçamento com dívidas</strong>
                {" "}não só é possível, como também é um caminho seguro
                para retomar a tranquilidade financeira.
              </Typography>

              <Typography sx={{ fontSize: 20, mb: 2 }}>
                Não existe fórmula mágica, mas <strong>existe estratégia e respiro.</strong>{" "}
                Organizar o orçamento mesmo com dívidas exige clareza, escolha e — acima de tudo — gentileza com sua própria jornada.
                Se precisar de ajuda para começar, considere ferramentas que facilitam o seu presente sem comprometer o seu futuro.
              </Typography>

              <Typography sx={{ fontSize: 20, mb: 2, fontWeight: "bold" }}>
                O parcelamento <span className="quita">Quita</span>,
                por exemplo, cabe no seu bolso. E cabe na sua vida.
              </Typography>
            </Grid>

            <Grid size={12} align="center">
              <BotaoSimular>CLIQUE AQUI E REALIZE UMA SIMULAÇÃO</BotaoSimular>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
}
